/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { useQuery } from '@apollo/client';
import type { LearnerProfile_Course } from '__generated__/graphql-types-do-not-use';

import { Divider, InlineNotification, ShowMoreList } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import CoursesSectionItem from 'bundles/account-profile/components/recruiting-profile-viewer/components/courses/CoursesSectionItem';
import { CredentialsSectionItemPlaceholder } from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionItem';
import CourseByIds from 'bundles/account-profile/components/recruiting-profile-viewer/queries/CourseByIds.graphql';
import type {
  CourseByIdsQuery,
  CourseByIdsQueryVariables,
} from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/CourseByIds';
import { getPartnerInfo } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/courseUtils';

import _t from 'i18n!nls/account-profile';

const MAX_ITEMS_TO_SHOW = 3;

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,

  divider: css`
    margin: var(--cds-spacing-200) 0;
  `,

  courseraCourseErrorNotification: css`
    margin-top: var(--cds-spacing-200);
  `,
};

const CoursesSectionBody = () => {
  const { isProfileLoading, profileState = {}, courseraHistoryFailedToLoad } = useContext(ProfileContext);

  const {
    courseraHistory = {
      completedCourses: [],
    },
  } = profileState;

  const { data: coursesData } = useQuery<CourseByIdsQuery, CourseByIdsQueryVariables>(CourseByIds, {
    variables: {
      ids: courseraHistory?.completedCourses?.map((course) => course.productId) || [],
    },
    skip: !courseraHistory?.completedCourses?.length,
    context: { clientName: 'gatewayGql' },
  });

  if (isProfileLoading) {
    return (
      <div role="status">
        <CredentialsSectionItemPlaceholder />
        <Divider color="light" css={styles.divider} />
        <CredentialsSectionItemPlaceholder />
        <Divider color="light" css={styles.divider} />
        <CredentialsSectionItemPlaceholder />
      </div>
    );
  }

  const noCourseraHistory = !courseraHistory?.completedCourses.length && !courseraHistoryFailedToLoad;

  if (!isProfileLoading && courseraHistory?.completedCourses?.length === 0 && noCourseraHistory) {
    return null;
  }

  const filterByUniqueId = (items: LearnerProfile_Course[]) => {
    const seenIds = new Set<string>();
    return items.filter((item) => {
      if (seenIds.has(item.productId)) {
        return false;
      }
      seenIds.add(item.productId);
      return true;
    });
  };

  const filteredCourses = courseraHistory?.completedCourses ? filterByUniqueId(courseraHistory?.completedCourses) : [];

  const totalItemsLength = filteredCourses.length;

  const ItemWrapperComponent =
    totalItemsLength > MAX_ITEMS_TO_SHOW
      ? ({ children, ...props }: { children: React.ReactNode[] }) => (
          <ShowMoreList
            numItemsToShow={MAX_ITEMS_TO_SHOW}
            controllerProps={{ itemTypePlural: _t('courses'), ariaLabel: _t('Show all courses') }}
            {...props}
          >
            {children}
          </ShowMoreList>
        )
      : ({ children, ...props }: { children: React.ReactNode }) => <div {...props}>{children}</div>;

  return (
    <div css={styles.wrapper}>
      {(totalItemsLength > 0 || courseraHistoryFailedToLoad) && (
        <ItemWrapperComponent>
          {filteredCourses.map((course, index) => {
            return (
              <div key={`${course.productId}`}>
                <CoursesSectionItem course={course} partner={getPartnerInfo(coursesData, course.productId)} />
                {index < totalItemsLength - 1 && <Divider color="light" css={styles.divider} />}
              </div>
            );
          })}
          {courseraHistoryFailedToLoad && (
            <InlineNotification
              severity="error"
              css={styles.courseraCourseErrorNotification}
              title={_t("We're sorry, we can't load the Coursera course information data right now")}
            >
              {_t('There was an error loading the Coursera course data. Please try again later.')}
            </InlineNotification>
          )}
        </ItemWrapperComponent>
      )}
    </div>
  );
};

export default CoursesSectionBody;
